import React, { useState, useEffect } from "react";
import { Modal, Button, FileInput } from "flowbite-react";
import { useDispatch } from "react-redux";
import { uploadFotosAction } from "../../actions/foto";
import Compressor from "compressorjs"; // Biblioteca para manipulação de imagens
import { HiOutlineCloudUpload } from "react-icons/hi";

const ModalFotos = ({ produto, isOpen, onClose }) => {
  const [fotos, setFotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch();

  // **Verifica se o arquivo é realmente uma imagem**
  const isValidImage = (file) => {
    return (
      file.type.startsWith("image/") && /\.(jpe?g|png|webp)$/i.test(file.name)
    );
  };

  // **Manipula a seleção de arquivos**
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    if (files.length + fotos.length > 10) {
      alert("Você pode enviar no máximo 10 fotos por vez.");
      return;
    }

    const validFiles = files.filter(isValidImage);
    if (validFiles.length !== files.length) {
      alert("Alguns arquivos foram rejeitados por não serem imagens válidas.");
    }

    setFotos([...fotos, ...validFiles]);
  };

  // **Redimensiona imagens com mais de 1200px de largura ou altura**
  const processImages = async (files) => {
    return Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          if (!isValidImage(file)) {
            reject("Arquivo inválido.");
            return;
          }

          const img = new Image();
          img.src = URL.createObjectURL(file);

          img.onload = () => {
            const { width, height } = img;

            if (width <= 1200 && height <= 1200) {
              resolve(file); // Se já está no tamanho certo, retorna o arquivo original
            } else {
              // Redimensiona para que a maior dimensão seja 1200px
              new Compressor(file, {
                maxWidth: width >= height ? 1200 : undefined,
                maxHeight: height > width ? 1200 : undefined,
                quality: 0.8,
                success: (compressedFile) => resolve(compressedFile),
                error: (err) => reject(err),
              });
            }
          };

          img.onerror = () => reject("Erro ao carregar a imagem.");
        });
      })
    );
  };

  // **Dispara o upload após redimensionar as imagens**
  const handleUpload = async () => {
    if (fotos.length === 0) {
      alert("Por favor, selecione ao menos uma foto.");
      return;
    }

    if (!produto?.id || !produto?.descricao) {
      alert("Erro: Produto sem ID ou descrição.");
      return;
    }

    try {
      setUploading(true);
      const processedFotos = await processImages(fotos);
      await dispatch(
        uploadFotosAction(produto.id, produto.descricao, processedFotos)
      );
      onClose();
    } catch (error) {
      alert("Erro ao processar as imagens. Tente novamente.");
      console.error("Erro ao processar imagens:", error);
    } finally {
      setUploading(false);
    }
  };

  // **Limpa os arquivos selecionados ao fechar o modal**
  useEffect(() => {
    if (!isOpen) {
      setFotos([]);
    }
  }, [isOpen]);

  return (
    <Modal show={isOpen} onClose={onClose} size="lg">
      <Modal.Header>{produto?.descricao || "Cadastrar Fotos"}</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Input de Arquivos */}
          <FileInput
            multiple
            accept="image/png, image/jpeg, image/webp"
            onChange={handleFileChange}
          />

          {/* Grid de Fotos */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            {fotos.length > 0 ? (
              fotos.map((foto, index) => (
                <div
                  key={index}
                  className="relative border rounded-lg overflow-hidden"
                >
                  <img
                    src={URL.createObjectURL(foto)}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-32 object-cover"
                  />
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm col-span-3 text-center">
                Você pode enviar até 10 fotos por vez.
              </p>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          color="blue"
          onClick={handleUpload}
          disabled={uploading || fotos.length === 0}
          isProcessing={uploading}
        >
          <HiOutlineCloudUpload className="w-5 h-5 mr-2" />
          {uploading ? "Processando fotos..." : "Enviar Fotos"}
        </Button>
        <Button color="gray" onClick={onClose} disabled={uploading}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalFotos;
